// #region Imports
import React, { useState, useEffect } from 'react';
import './ClientesTable-Component.scss';
import iconSearch from '../../../assets/lupa.svg';
import iconEdit from '../../../assets/edit.svg';
import iconTrash from '../../../assets/trash.svg';
import { formatDateISO, formatCurrency, formatCPF, formatPhone } from '../../../../shared/helpers';
// #endregion

export default function ClientesTable({
	loading = false,
	stats,
	filters,
	data = [],
	total = 0,
	page = 1,
	pageSize = 10,
	onFiltersChange = () => {},
	onPageChange = () => {},
	onCreate = () => {},
	onEdit = () => {},
	onDelete = () => {},
}) {
	// #region Estado local (draft de filtros)
	const [draftFilters, setDraftFilters] = useState(filters);
	useEffect(() => { setDraftFilters(filters); }, [filters]);
	// #endregion

	// #region Handlers filtros/paginação
	function updateDraft(patch) { setDraftFilters(prev => ({ ...prev, ...patch })); }
	function applyFilters() { onFiltersChange(draftFilters); }
	function clearFilters() {
		const cleared = { q: '', sexo: 'TODOS', saldo: 'TODOS' };
		setDraftFilters(cleared);
		onFiltersChange(cleared);
	}
	const totalPages = Math.max(1, Math.ceil(total / pageSize));
	function jumpToPage(p) { const n = Math.min(Math.max(1, p), totalPages); onPageChange(n); }
	// #endregion

	// #region Render helpers
	const renderBody = () => {
		if (loading) {
			return Array.from({ length: 6 }).map((_, i) => (
				<tr key={`sk-${i}`} className="skeleton-row" aria-hidden="true">
					<td colSpan="7">
						<div className="sk-line w40" />
						<div className="sk-line w25" />
					</td>
				</tr>
			));
		}
		if (data.length === 0) {
			return (
				<>
					<tr>
						<td colSpan="7" className="empty-cell">Nenhum cliente encontrado.</td>
					</tr>
					{Array.from({ length: 5 }).map((_, i) => (
						<tr key={`empty-fill-${i}`} className="row-filler" aria-hidden="true"><td colSpan="7" /></tr>
					))}
				</>
			);
		}
		return (
			<>
				{data.map(c => {
					const saldo = c.saldo_devedor || 0;
					const devendo = saldo > 0;
					return (
						<tr key={c.id}>
							<td>
								<div className="cell-main">
									<span className="cell-title">{c.nome}</span>
									{c.observacoes && <span className="cell-sub">{c.observacoes}</span>}
								</div>
							</td>
							<td>{formatCPF(c.cpf)}</td>
							<td>{formatPhone(c.telefone)}</td>
							<td>
								<span className={`tag tag-${c.sexo === 'F' ? 'fem' : 'masc'}`}>
									{c.sexo === 'F' ? 'Feminino' : 'Masculino'}
								</span>
							</td>
							<td>{formatDateISO(c.data_nascimento)}</td>
							<td>
								<span className={`tag ${devendo ? 'tag-danger-outline' : 'tag-success-outline'}`}>
									{devendo ? formatCurrency(saldo) : 'Em dia'}
								</span>
							</td>
							<td className="actions">
								<button type="button" className="icon-btn" onClick={() => onEdit(c.id)} aria-label={`Editar ${c.nome}`}>
									<img src={iconEdit} alt="" />
								</button>
								<button
									type="button"
									className="icon-btn danger"
									onClick={() => { if (window.confirm('Remover cliente?')) onDelete(c.id); }}
									aria-label={`Excluir ${c.nome}`}
								>
									<img src={iconTrash} alt="" />
								</button>
							</td>
						</tr>
					);
				})}
				{data.length < 6 && Array.from({ length: Math.max(0, 6 - data.length) }).map((_, i) => (
					<tr key={`filler-${i}`} className="row-filler" aria-hidden="true"><td colSpan="7" /></tr>
				))}
			</>
		);
	};
	// #endregion

	// #region Render
	return (
		<div className="clientes-module">
			<div className="clientes-head">
				<h2 className="clientes-title">Clientes</h2>
				<button type="button" onClick={onCreate} className="btn btn-primary">
					<span className="btn-icon">+</span> Novo Cliente
				</button>
			</div>
			<div className="kpi-grid" role="region" aria-label="Indicadores de clientes">
				<div className="kpi-card">
					<span className="kpi-label">Total de Clientes</span>
					<span className="kpi-value">{stats.totalClientes}</span>
				</div>
				<div className="kpi-card">
					<span className="kpi-label">Com Saldo Devedor</span>
					<span className="kpi-value kpi-danger">{stats.totalComSaldo}</span>
				</div>
				<div className="kpi-card">
					<span className="kpi-label">Total a Receber</span>
					<span className="kpi-value kpi-warning">{formatCurrency(stats.totalValorReceber)}</span>
				</div>
			</div>
			<div className="filter-panel" role="form" aria-label="Filtros de clientes">
				<div className="filter-header">
					<img src={iconSearch} alt="" className="filter-icon" />
					<span className="filter-title">Filtros</span>
				</div>
				<div className="filter-grid">
					<div className="filter-field">
						<label htmlFor="f-q">Buscar</label>
						<input
							id="f-q"
							placeholder="Nome, CPF ou telefone"
							value={draftFilters.q}
							onChange={e => updateDraft({ q: e.target.value })}
							aria-label="Buscar por texto"
							disabled={loading}
						/>
					</div>
					<div className="filter-field">
						<label htmlFor="f-sexo">Sexo</label>
						<select
							id="f-sexo"
							value={draftFilters.sexo}
							onChange={e => updateDraft({ sexo: e.target.value })}
							aria-label="Filtrar por sexo"
							disabled={loading}
						>
							<option value="TODOS">Todos</option>
							<option value="F">Feminino</option>
							<option value="M">Masculino</option>
						</select>
					</div>
						<div className="filter-field">
						<label htmlFor="f-saldo">Saldo</label>
						<select
							id="f-saldo"
							value={draftFilters.saldo}
							onChange={e => updateDraft({ saldo: e.target.value })}
							aria-label="Filtrar por saldo"
							disabled={loading}
						>
							<option value="TODOS">Todos</option>
							<option value="EM_DIA">Em dia</option>
							<option value="EM_ABERTO">Em aberto</option>
						</select>
					</div>
					<div className="filter-actions">
						<button type="button" className="btn btn-primary" onClick={applyFilters} aria-label="Aplicar filtros" disabled={loading}>Filtrar</button>
						<button type="button" className="btn btn-ghost" onClick={clearFilters} aria-label="Limpar filtros" disabled={loading}>Limpar</button>
					</div>
				</div>
			</div>
			<div className="table-wrapper" role="region" aria-label="Lista de clientes">
				<div className="table-container">
					<table className="ct-table">
						<thead>
							<tr>
								<th>Nome</th>
								<th>CPF</th>
								<th>Telefone</th>
								<th>Sexo</th>
								<th>Nascimento</th>
								<th>Saldo</th>
								<th>Ações</th>
							</tr>
						</thead>
						<tbody>{renderBody()}</tbody>
					</table>
				</div>
				<div className="ct-footer" role="navigation" aria-label="Paginação">
					<div className="result-count">Total: {total}</div>
					<div className="pagination">
						<button type="button" onClick={() => jumpToPage(1)} disabled={page === 1 || loading} aria-label="Primeira página">&laquo;</button>
						<button type="button" onClick={() => jumpToPage(page - 1)} disabled={page === 1 || loading} aria-label="Página anterior">&lsaquo;</button>
						<label className="page-jump">
							<span className="sr-only">Página atual</span>
							<input
								value={String(page)}
								onChange={e => { const v = e.target.value.replace(/\D/g, ''); if (v) jumpToPage(Number(v)); }}
								aria-label="Ir para página"
								inputMode="numeric"
								disabled={loading}
							/>
							<span className="total-pages">/ {totalPages}</span>
						</label>
						<button type="button" onClick={() => jumpToPage(page + 1)} disabled={page === totalPages || loading} aria-label="Próxima página">&rsaquo;</button>
						<button type="button" onClick={() => jumpToPage(totalPages)} disabled={page === totalPages || loading} aria-label="Última página">&raquo;</button>
					</div>
				</div>
			</div>
		</div>
	);
	// #endregion
}
